import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Camera, Check, Loader2, Pencil } from 'lucide-react';
import { meUpdateInputSchema, type MeUpdateInput } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField } from '@/components/ui/floating-field';
import { UserAvatar } from '@/components/users/user-avatar';
import { useUpdateMe, useUploadMyAvatar } from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';

interface MeFormModalProps {
  open: boolean;
  onClose: () => void;
}

export function MeFormModal({ open, onClose }: MeFormModalProps) {
  const me = useAuthStore((s) => s.user);
  const update = useUpdateMe();
  const uploadAvatar = useUploadMyAvatar();
  const fileRef = useRef<HTMLInputElement>(null);
  const form = useForm<MeUpdateInput>({
    resolver: zodResolver(meUpdateInputSchema),
    defaultValues: { name: me?.name ?? '', email: me?.email ?? '' },
  });

  useEffect(() => {
    if (open && me) form.reset({ name: me.name, email: me.email });
  }, [open, me, form]);

  // Avatar usa endpoint próprio (converte pra WebP no servidor) e persiste na hora,
  // independente do botão "Salvar" do nome/e-mail.
  function handleAvatarPick(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    uploadAvatar.mutate(file, {
      onSuccess: () => toast.success('Foto atualizada'),
      onError: () => toast.error('Erro ao enviar foto'),
    });
  }

  async function onSubmit(data: MeUpdateInput) {
    try {
      await update.mutateAsync(data);
      toast.success('Perfil atualizado');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      toast.error(msg);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Editar perfil">
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Identidade — avatar clicável (troca foto) + papel */}
        {me && (
          <div className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadAvatar.isPending}
              className="group relative shrink-0 rounded-full"
              aria-label="Trocar foto de perfil"
            >
              <UserAvatar
                name={me.name}
                colorHex={me.color_hex}
                url={me.avatar_url}
                size="lg"
              />
              <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40 opacity-0 transition group-hover:opacity-100">
                {me.avatar_url ? (
                  <Pencil className="h-4 w-4 text-white" />
                ) : (
                  <Camera className="h-4 w-4 text-white" />
                )}
              </span>
              {uploadAvatar.isPending && (
                <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </span>
              )}
            </button>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{me.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {me.role}
              </p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadAvatar.isPending}
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary disabled:opacity-60"
              >
                <Camera className="h-3 w-3" />
                {me.avatar_url ? 'Trocar foto' : 'Adicionar foto'}
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleAvatarPick(f);
                e.target.value = '';
              }}
            />
          </div>
        )}

        <FloatingField
          label="Nome"
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
        <FloatingField
          label="E-mail"
          type="email"
          autoComplete="off"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium transition active:scale-[0.97] hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="flex flex-[2] items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            {update.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}
